import type {
  AdminDesktopGpus,
  DesktopMachine,
  DesktopModelTotal,
  DesktopPolicyState,
} from './desktop-types';
import { anyModelName, nameOf } from './desktop-words';
import { cardName } from './gpu-words';

/**
 * A machine on Desktop GPUs: its cards (one worker each, slot 1 first), its policy, and its state as a whole: offline
 * when no card is connected, paused when the machine is paused or every connected card is, else online.
 */
export interface MachineGroup {
  id: string;
  cards: DesktopMachine[];
  policy: DesktopPolicyState | undefined;
  state: DesktopMachine['state'];
}

/** The machine a worker id is on: the id before `~` (services/Paige.Relay.Protocol/WorkerIds.cs). */
export function machineOf(worker: string): string {
  const mark = worker.indexOf('~');
  return mark < 0 ? worker : worker.slice(0, mark);
}

function stateOf(
  cards: DesktopMachine[],
  policy: DesktopPolicyState | undefined,
): DesktopMachine['state'] {
  const connected = cards.filter((c) => c.state !== 'offline');
  if (connected.length === 0) return 'offline';
  if (policy?.policy?.paused || connected.every((c) => c.state === 'paused')) return 'paused';
  return 'online';
}

const ORDER: Record<DesktopMachine['state'], number> = { offline: 0, paused: 1, online: 2 };

/** The view's cards grouped by machine, offline first, then paused, then online, then by name. */
export function groupMachines(view: AdminDesktopGpus): MachineGroup[] {
  const byId = new Map<string, DesktopMachine[]>();
  for (const m of view.machines) {
    const id = m.machineId || machineOf(m.worker);
    byId.set(id, [...(byId.get(id) ?? []), m]);
  }
  return [...byId.entries()]
    .map(([id, cards]) => {
      const sorted = [...cards].sort((a, b) => a.slot - b.slot);
      const policy = (view.policies ?? []).find((p) => p.machine === id);
      return { id, cards: sorted, policy, state: stateOf(sorted, policy) };
    })
    .sort(
      (a, b) =>
        ORDER[a.state] - ORDER[b.state] ||
        groupName(a).localeCompare(groupName(b), undefined, { sensitivity: 'base' }),
    );
}

/** What people call the machine: its first card's given name, else the machine's own. */
export function groupName(g: MachineGroup): string {
  const first = g.cards[0];
  return first ? nameOf(first) : g.id;
}

/** The machine's cards in one line: "RTX 4090 · RTX 5060 Ti". */
export function cardsLine(g: MachineGroup): string {
  return g.cards.map((c) => cardName(c.card)).join(' · ');
}

/** A span's work across the machine's cards, per model. */
export function machineTotals(
  g: MachineGroup,
  span: 'today' | 'week' | 'month',
): DesktopModelTotal[] {
  const sum = new Map<string, DesktopModelTotal>();
  for (const t of g.cards.flatMap((c) => c[span])) {
    const was = sum.get(t.model) ?? { model: t.model, calls: 0, errors: 0, busySeconds: 0 };
    sum.set(t.model, {
      model: t.model,
      calls: was.calls + t.calls,
      errors: was.errors + t.errors,
      busySeconds: was.busySeconds + t.busySeconds,
    });
  }
  return [...sum.values()];
}

/** Counts for the strip on top, per machine. */
export function groupCounts(groups: MachineGroup[]): Record<DesktopMachine['state'], number> {
  return {
    online: groups.filter((g) => g.state === 'online').length,
    paused: groups.filter((g) => g.state === 'paused').length,
    offline: groups.filter((g) => g.state === 'offline').length,
  };
}

/** Machines a filter keeps: by machine, card or model name, and by state. */
export function matchingGroups(
  groups: MachineGroup[],
  text: string,
  state: DesktopMachine['state'] | 'all',
): MachineGroup[] {
  const q = text.trim().toLowerCase();
  return groups.filter(
    (g) =>
      (state === 'all' || g.state === state) &&
      (!q ||
        g.cards.some((m) =>
          [m.machine, m.displayName ?? '', m.card, ...m.models.map(anyModelName)].some((s) =>
            s.toLowerCase().includes(q),
          ),
        )),
  );
}
